import type { Order } from '$lib/types/order';
import type { popupType } from '$lib/types/popup';
import type { Position } from '$lib/types/position';
import {
	LimitStatus,
	type OrderExecutionType,
	type PostTradeDetails
} from '$lib/types/userInteract';
import BigNumber from 'bignumber.js';
import { writable, derived, type Writable, type Readable } from 'svelte/store';
import type { CreateStepTitles } from './createSCWallet/createSCWalletTypes';
import { marketPrices } from '$lib/util/marketHelper';
import { addDollarSign, getNumBackInUnits } from '$lib/util/util';
import { markets } from '$lib/home/store';
import { marketsConfig } from '$lib/config/markets';
import {
	LIQUIDATION_PRICE_GAP_PERCENTAGE,
	MIN_MARGIN_REQUIREMENT,
	SAFE_MARGIN_REQUIREMENT
} from '$lib/home/constants';
import { userMarginList } from '$lib/home/userInteract/store';
import type { Market } from '$lib/types/market';

export const popupModalOpen: Writable<boolean> = writable(false);
export const popupModalType: Writable<popupType> = writable('CreateWallet');
export const popupModalData: Writable<any> = writable();

//Adjust Margin
export const adjustMarginTokenSelectorOpen: Writable<boolean> = writable(false);

//Close Position
export const currentClosePosition: Writable<Position> = writable();
export const closePositionOrderType: Writable<OrderExecutionType> = writable('Market');
export const closePositionInput: Writable<string> = writable('0');
export const limitInputForClosePosition: Writable<string> = writable('0');
export const triggerInputForClosePosition: Writable<string> = writable('0');
export const ClosePositionLimitStatus: Writable<LimitStatus> = writable(LimitStatus.Ok);

export const closeLongOrShort: Readable<'Long' | 'Short'> = derived(
	currentClosePosition,
	($closePosition) => {
		if ($closePosition) {
			if (new BigNumber($closePosition.size).lte(0)) return 'Long';
			else return 'Short';
		}
		return 'Long';
	}
);

export const loadingClosePositionPostTradeDetails: Writable<boolean> = writable(false);
export const closePositionPostTradeDetails: Writable<PostTradeDetails> = writable();

//Create SC Wallet
export const createScWalletActiveStep: Writable<CreateStepTitles> = writable('1. Create Account');
// export const

//TOS
export const tosBeingSigned: Writable<boolean> = writable(false);
export const tosBeingChecked: Writable<boolean> = writable(false);
export const creatingSmartContractWallet: Writable<boolean> = writable(false);

//TPSL
export const positionData: Writable<Position> = writable();
export const loadingTPSL: Writable<boolean> = writable(false);
export const takeProfitInput: Writable<string> = writable('0');
export const stopLossInput: Writable<string> = writable('0');
export const gainPercentage: Writable<string> = writable('0');
export const lossPercentage: Writable<string> = writable('0');
export const isEntirePosition: Writable<boolean> = writable(true);
export const tpslPostTradeDetails: Writable<PostTradeDetails> = writable();
export const tpslPostTradeDetailsLoading: Writable<boolean> = writable(false);
export const stopLossRemoved: Writable<boolean> = writable(false);
export const takeProfitRemoved: Writable<boolean> = writable(false);

export const tpslFees: Readable<string> = derived(
	[takeProfitInput, stopLossInput, tpslPostTradeDetails, positionData, marketPrices],
	([$takeProfitInput, $stopLossInput, $tpslPostTradeDetails, $positionData, $marketPrices]) => {
		const KEEPER_DEPOSIT = '4.00';
		let isTPSLOrder = $takeProfitInput != '0' || $stopLossInput != '0';
		if (!isTPSLOrder) return '0';

		let tpslOrderFee = isTPSLOrder
			? new BigNumber(
					getNumBackInUnits(
						new BigNumber($positionData.size)
							.multipliedBy($marketPrices[$positionData.marketIdentifier])
							.div(1e18)
							.abs()
							.toString(),
						18,
						2
					)
			  )
					.multipliedBy(
						getNumBackInUnits($tpslPostTradeDetails?.advancedOrderOrderFee || '0', 18, 8)
					)
					.toString()
			: '0';

		let standardOrderFee = $tpslPostTradeDetails?.fee
			? new BigNumber($tpslPostTradeDetails.fee)
					.plus(new BigNumber($tpslPostTradeDetails.keeperFee))
					.toString()
			: '0';

		let totalFee = new BigNumber(standardOrderFee)
			.plus(new BigNumber(tpslOrderFee))
			.plus(isTPSLOrder ? new BigNumber($tpslPostTradeDetails?.advancedOrderFixedFee || '0') : '0')
			.toString();
		return totalFee;
	}
);

export const estimatedProfit: Readable<string> = derived(
	[takeProfitInput, positionData, tpslFees],
	([$takeProfitInput, $positionData, $tpslFees]) => {
		if ($positionData && new BigNumber($takeProfitInput).gt(0)) {
			const avgEntryPrice = new BigNumber($positionData.avgEntryPrice);
			if (avgEntryPrice) {
				const sizeInNative = getNumBackInUnits(
					$positionData.size,
					18,
					marketsConfig[$positionData.asset].showDecimals + 2
				);

				if (new BigNumber($positionData.size).isPositive()) {
					const priceDiff = new BigNumber($takeProfitInput).minus(avgEntryPrice);
					return priceDiff
						.multipliedBy(new BigNumber(sizeInNative).abs())
						.minus(new BigNumber($tpslFees))
						.div(1e18)
						.toFixed(marketsConfig[$positionData.asset].showDecimals);
				} else {
					const priceDiff = new BigNumber(avgEntryPrice).minus($takeProfitInput);
					return priceDiff
						.multipliedBy(new BigNumber(sizeInNative).abs())
						.minus(new BigNumber($tpslFees))
						.div(1e18)
						.toFixed(marketsConfig[$positionData.asset].showDecimals);
				}
			}
		}
		return '0';
	}
);

export const estimatedLoss: Readable<string> = derived(
	[stopLossInput, positionData, tpslFees],
	([$stopLossInput, $positionData, $tpslFees]) => {
		if ($positionData && new BigNumber($stopLossInput).gt(0)) {
			const avgEntryPrice = new BigNumber($positionData.avgEntryPrice);
			if (avgEntryPrice) {
				const sizeInNative = getNumBackInUnits(
					$positionData.size,
					18,
					marketsConfig[$positionData.asset].showDecimals + 2
				);

				if (new BigNumber($positionData.size).isPositive()) {
					const priceDiff = new BigNumber(avgEntryPrice).minus($stopLossInput);
					return priceDiff
						.multipliedBy(new BigNumber(sizeInNative).abs())
						.plus(new BigNumber($tpslFees))
						.div(1e18)
						.toFixed(marketsConfig[$positionData.asset].showDecimals)
						.toString();
				} else {
					const priceDiff = new BigNumber($stopLossInput).minus(avgEntryPrice);
					return priceDiff
						.multipliedBy(new BigNumber(sizeInNative).abs())
						.plus(new BigNumber($tpslFees))
						.div(1e18)
						.toFixed(marketsConfig[$positionData.asset].showDecimals)
						.toString();
				}
			}
		}
		return '0';
	}
);

export const isTakeProfitInputValid: Readable<boolean> = derived(
	[takeProfitInput, positionData, markets, gainPercentage],
	([$takeProfitInput, $positionData, $markets, $gainPercentage]) => {
		if (
			$positionData &&
			new BigNumber($takeProfitInput).gt(0) &&
			!new BigNumber($gainPercentage).eq(0)
		) {
			const targetMarket = $markets.find(
				(m) => m.marketIdentifier === $positionData.marketIdentifier
			);
			if (targetMarket?.safePrice) {
				if (new BigNumber($positionData.size).isPositive()) {
					return new BigNumber($takeProfitInput).gt(new BigNumber(targetMarket?.safePrice));
				} else {
					return new BigNumber($takeProfitInput).lt(new BigNumber(targetMarket?.safePrice));
				}
			}
		}
		return true;
	}
);

export const isStopLossInputValid: Readable<boolean> = derived(
	[
		stopLossInput,
		positionData,
		markets,
		lossPercentage,
		estimatedLoss,
		userMarginList,
		stopLossRemoved
	],
	([
		$stopLossInput,
		$positionData,
		$markets,
		$lossPercentage,
		$estimatedLoss,
		$userMarginList,
		$stopLossRemoved
	]) => {
		if ($stopLossRemoved) return true;
		if (
			$positionData &&
			new BigNumber($stopLossInput).gte(0) &&
			!new BigNumber($lossPercentage).eq(0)
		) {
			const targetMarket = $markets.find(
				(m) => m.marketIdentifier === $positionData.marketIdentifier
			);
			if (
				new BigNumber($stopLossInput).gt(0) &&
				new BigNumber($positionData?.liquidationPrice).gt(0)
			) {
				if (
					new BigNumber($positionData.size).isPositive() &&
					new BigNumber($stopLossInput).lte(new BigNumber($positionData.liquidationPrice))
				) {
					stopLossErrorMessage.set('SL cannot be less than liq. price');
					stopLossErrorDescription.set('');
					return false;
				} else if (
					new BigNumber($positionData.size).isNegative() &&
					new BigNumber($stopLossInput).gte(new BigNumber($positionData.liquidationPrice))
				) {
					stopLossErrorMessage.set('SL cannot be greater than liq. price');
					stopLossErrorDescription.set('');
					return false;
				}
			}
			if (targetMarket?.safePrice) {
				if (new BigNumber($positionData.size).isPositive()) {
					if (new BigNumber($stopLossInput).gte(new BigNumber(targetMarket?.safePrice))) {
						stopLossErrorMessage.set(
							`Max SL: ${addDollarSign(
								getNumBackInUnits(
									new BigNumber(targetMarket?.safePrice).toString(),
									18,
									targetMarket ? marketsConfig[targetMarket?.asset].showDecimals : 4
								),
								targetMarket ? marketsConfig[targetMarket?.asset].showDecimals : 4
							)}`
						);
						stopLossErrorDescription.set('');
						return false;
					}
				} else if (new BigNumber($stopLossInput).lte(new BigNumber(targetMarket?.safePrice))) {
					stopLossErrorMessage.set(
						`Min SL: ${addDollarSign(
							getNumBackInUnits(
								new BigNumber(targetMarket?.safePrice).toString(),
								18,
								targetMarket ? marketsConfig[targetMarket?.asset].showDecimals : 4
							),
							targetMarket ? marketsConfig[targetMarket?.asset].showDecimals : 4
						)}`
					);
					stopLossErrorDescription.set('');
					return false;
				}
			}
			let positionMargin =
				$userMarginList.find((margin) => margin.marketIdentifier === targetMarket?.marketIdentifier)
					?.depositedMargin || $positionData.margin;
			if (
				new BigNumber(getNumBackInUnits(positionMargin, 18))
					.minus(new BigNumber($estimatedLoss))
					.lt(new BigNumber(MIN_MARGIN_REQUIREMENT))
			) {
				stopLossErrorMessage.set('SL below min. margin requirement');
				stopLossErrorDescription.set(
					`Your margin at the time of Stop Loss will be less than the minimum margin requirement of $40. Please increase your margin or adjust your SL accordingly.`
				);
				return false;
			}
		}
		stopLossErrorMessage.set('');
		stopLossErrorDescription.set('');
		return true;
	}
);

export const isStopLossInputSafe: Readable<boolean> = derived(
	[stopLossInput, positionData, estimatedLoss],
	([$stopLossInput, $positionData, $estimatedLoss]) => {
		if (new BigNumber($stopLossInput).gt(0)) {
			const liquidationPrice = $positionData?.liquidationPrice;
			if (new BigNumber(liquidationPrice).gt(0)) {
				const stopLossGapInPercentage = new BigNumber($stopLossInput)
					.minus(new BigNumber(liquidationPrice))
					.abs()
					.div(new BigNumber(liquidationPrice))
					.times(100)
					.toFixed(2);
				if (
					new BigNumber(stopLossGapInPercentage).lt(new BigNumber(LIQUIDATION_PRICE_GAP_PERCENTAGE))
				) {
					stopLossWarningMessage.set('SL too close to Liq. Price');
					return false;
				}
			}
			if (
				new BigNumber(getNumBackInUnits($positionData.margin, 18))
					.minus(new BigNumber($estimatedLoss))
					.lt(new BigNumber(SAFE_MARGIN_REQUIREMENT))
			) {
				stopLossWarningMessage.set('SL too close to Min. Margin');
				return false;
			}
		}
		return true;
	}
);

export const stopLossWarningMessage: Writable<string> = writable('');
export const stopLossErrorMessage: Writable<string> = writable('');
export const stopLossErrorDescription: Writable<string> = writable('');
